// WorkflowGraphPopup.tsx - Visual workflow progression graph with multi-company cycle support
import React, { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface WorkflowGraphPopupProps {
    isOpen: boolean;
    onClose: () => void;
    gatePassId: number;
    getAccessToken: () => Promise<string>;
    requesterName?: string;
    companyName?: string;
}

type NodeStatus = 'completed' | 'active' | 'pending' | 'rejected' | 'returned' | 'skipped';

interface TaskSummary {
    taskId: number;
    assignedUserName: string;
    assignedUserEmail: string;
    assignedCompanyName?: string;
    assignedCompanyId?: number;
    taskStatus: string;
    assignedAt: string;
    completedAt: string | null;
    completedBy?: string;
    comments?: string | null;
}

interface CompanyCycle {
    cycleNumber: number;
    companyName: string;
    companyId: number;
    approvers: TaskSummary[];
    reviewers: TaskSummary[];
    reviewerCompanyName?: string | null;
    reviewerCompanyId?: number | null;
}

interface GateBranch {
    gateId: number;
    gateNumber: string;
    locationName?: string;
    isExcluded: boolean;
    exclusionReason?: string | null;
    reviewerTasks: TaskSummary[];
    approverTasks: TaskSummary[];
}

interface AuditEntry {
    action: string;
    performedBy: string;
    performedByEmail: string;
    companyName?: string;
    performedAt: string;
    assignedTo?: string;
    comments?: string;
    taskType?: string;
}

interface WorkflowGraphData {
    currentState: string;
    submitter: {
        name: string;
        email: string;
        companyName: string;
        submittedAt: string;
    };
    companyCycles: CompanyCycle[];
    gateBranches: GateBranch[];
    securityStage: { tasks: TaskSummary[] } | null;
    totalTasks: number;
    auditTrail?: AuditEntry[];
    workflowPasses?: WorkflowPass[];
}

interface WorkflowPass {
    passNumber: number;
    companyApprovers: TaskSummary[];
    companyReviewers: TaskSummary[];
    gateBranches: GateBranch[];
    securityTasks: TaskSummary[];
    companyName: string;
    reviewerCompanyName?: string | null;
    resubmitEvent?: { performer: string; timestamp: string } | null;
    returnEvent?: { performer: string; timestamp: string; stage: string; comments?: string } | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function resolveTaskGroupStatus(tasks: TaskSummary[], currentState: string, activeStates: string[]): NodeStatus {
    if (tasks.length === 0) return 'pending';
    const hasCompleted = tasks.some(t => t.taskStatus === 'COMPLETED');
    const hasRejected = tasks.some(t => t.taskStatus === 'REJECTED');
    const hasPending = tasks.some(t => t.taskStatus === 'PENDING');
    const allCancelled = tasks.every(t => t.taskStatus === 'CANCELLED' || t.taskStatus === 'CANCELLED_BY_USER');

    if (hasRejected) return 'rejected';
    if (hasCompleted && !hasPending) return 'completed';
    if (hasPending && activeStates.includes(currentState)) return 'active';
    if (hasPending) return 'pending';
    if (allCancelled) return 'skipped';
    if (tasks.every(t => t.taskStatus === 'CANCELLED')) return 'completed';
    return 'completed';
}

function formatTime(dateStr: string | null | undefined): string | undefined {
    if (!dateStr) return undefined;
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getPerformerName(tasks: TaskSummary[]): string | undefined {
    if (!tasks || tasks.length === 0) return undefined;
    
    const names = [...new Set(
        tasks.map(t => t.assignedUserName || t.assignedUserEmail?.split('@')[0] || '').filter(Boolean)
    )];
    
    if (names.length === 0) return undefined;
    if (names.length === 1) return names[0];
    return names.join(', ');
}

function getCompletedTime(tasks: TaskSummary[]): string | undefined {
    const completed = tasks.find(t => t.taskStatus === 'COMPLETED');
    return formatTime(completed?.completedAt || completed?.assignedAt);
}

function getComments(tasks: TaskSummary[]): string | undefined {
    const withComment = tasks.find(t => (t.taskStatus === 'COMPLETED' || t.taskStatus === 'REJECTED') && t.comments);
    return withComment?.comments || undefined;
}

// ─── Split a single pass's CA/CR tasks into sequential stages ────────────
interface CompanyStage {
    approvers: TaskSummary[];
    reviewers: TaskSummary[];
}
function splitPassIntoStages(approvers: TaskSummary[], reviewers: TaskSummary[]): CompanyStage[] {
    if (approvers.length === 0 && reviewers.length === 0) return [];
    const allTasks = [
        ...approvers.map(t => ({ task: t, kind: 'CA' as const })),
        ...reviewers.map(t => ({ task: t, kind: 'CR' as const })),
    ].sort((a, b) => a.task.taskId - b.task.taskId);
    const groups: { kind: 'CA' | 'CR'; tasks: TaskSummary[] }[] = [];
    for (const item of allTasks) {
        const last = groups[groups.length - 1];
        if (last && last.kind === item.kind) {
            last.tasks.push(item.task);
        } else {
            groups.push({ kind: item.kind, tasks: [item.task] });
        }
    }
    const stages: CompanyStage[] = [];
    let i = 0;
    while (i < groups.length) {
        const stage: CompanyStage = { approvers: [], reviewers: [] };
        if (groups[i].kind === 'CA') {
            stage.approvers = groups[i].tasks;
            i++;
            if (i < groups.length && groups[i].kind === 'CR') {
                stage.reviewers = groups[i].tasks;
                i++;
            }
        } else {
            stage.reviewers = groups[i].tasks;
            i++;
        }
        stages.push(stage);
    }
    return stages;
}





// ─── Styles ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<NodeStatus, { bg: string; border: string; text: string; glow: string }> = {
    completed: { bg: '#f7f7f7', border: '#c8c8c8', text: '#1a1a1a', glow: 'none' },
    active:    { bg: '#f0f4ff', border: '#3b82f6', text: '#1e3a8a', glow: '0 0 0 4px rgba(59,130,246,0.18)' },
    pending:   { bg: '#f4f4f4', border: '#c8c8c8', text: '#5a5a5a', glow: 'none' },
    rejected:  { bg: '#fef2f2', border: '#f87171', text: '#991b1b', glow: '0 0 0 3px rgba(248,113,113,0.15)' },
    returned:  { bg: '#fef3c7', border: '#fbbf24', text: '#92400e', glow: '0 0 0 3px rgba(251,191,36,0.15)' },
    skipped:   { bg: '#f0f0f0', border: '#d4d4d4', text: '#8a8a8a', glow: 'none' },
};

// ─── Node Icons (enterprise SVG, color-aware) ────────────────────────────────

const NodeIcon: React.FC<{ type: string; size?: number; color?: string }> = ({ type, size = 22, color = '#6b7280' }) => {
    const s = { width: size, height: size };
    switch (type) {
        case 'submit':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/></svg>;
        case 'company':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>;
        case 'reviewer':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
        case 'gate-review':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>;
        case 'gate-approve':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
        case 'security':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        case 'approved':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
        case 'rejected':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
        case 'finish':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
        case 'workflow':
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
        case 'person':
            return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
        case 'person-check':
            return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>;
        default:
            return <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8"><circle cx="12" cy="12" r="9"/></svg>;
    }
};

// ─── Node Component ──────────────────────────────────────────────────────────

interface NodeProps {
    icon: string;
    label: string;
    sublabel?: string;
    status: NodeStatus;
    performer?: string;
    timestamp?: string;
    compact?: boolean;
    comment?: string;
    assignedTime?: string;
    tasks?: TaskSummary[];
    statusLabel?: string; // Override the default status badge text (e.g. '✓ Resubmitted')
}

const GraphNode: React.FC<NodeProps> = ({ icon, label, sublabel, status, performer, timestamp, compact, comment, assignedTime, tasks, statusLabel }) => {
    const c = STATUS_COLORS[status];
    const isActive = status === 'active';
    const isDone = status === 'completed' || status === 'returned' || status === 'rejected';
    
    // Build a set of emails/names that actually completed the task
    const completedNames = new Set(
        (tasks || [])
            .filter(t => t.taskStatus === 'COMPLETED')
            .map(t => t.assignedUserName || t.assignedUserEmail?.split('@')[0] || '')
            .filter(Boolean)
    );

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0px',
            minWidth: compact ? '120px' : '180px', maxWidth: compact ? '150px' : '220px',
        }}>
            <div style={{
                backgroundColor: c.bg, border: `2px solid ${c.border}`, borderRadius: '14px',
                padding: compact ? '12px 14px 10px' : '16px 18px 12px', textAlign: 'center',
                boxShadow: c.glow, width: '100%', transition: 'all 0.3s ease',
                ...(isActive ? { animation: 'subtlePulse 2.5s ease-in-out infinite' } : {}),
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '6px' }}>
                    <NodeIcon type={icon} size={compact ? 18 : 22} color={c.text} />
                </div>
                <div style={{
                    fontWeight: '700', fontSize: compact ? '11px' : '13px', color: c.text,
                    lineHeight: '1.3', letterSpacing: '0.01em',
                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                }}>
                    {label}
                </div>
                {sublabel && (
                    <div style={{
                        fontSize: '10px', color: c.text, opacity: 0.7, marginTop: '2px',
                        fontWeight: '600', fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                    }}>
                        {sublabel}
                    </div>
                )}

                {/* Performers inside the node */}
                {performer && (
                    <div style={{ marginTop: '8px', borderTop: `1px solid ${c.border}40`, paddingTop: '6px' }}>
                        {(() => {
                            const names = performer.split(', ');
                            const renderName = (name: string) => {
                                const isCompleter = completedNames.has(name);
                                return (
                                    <div style={{
                                        fontSize: '10px', fontWeight: '600',
                                        color: isCompleter ? '#F3694A' : c.text,
                                        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
                                    }}>
                                        <NodeIcon type={isCompleter ? 'person-check' : 'person'} color={isCompleter ? '#F3694A' : '#94a3b8'} />
                                        <span>{name}</span>
                                    </div>
                                );
                            };

                            if (names.length <= 1) {
                                return renderName(names[0]);
                            }
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                                    {names.map((name: string, i: number) => (
                                        <React.Fragment key={i}>{renderName(name)}</React.Fragment>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* Status badge OR comment */}
                <div style={{ marginTop: '8px' }}>
                    {comment && isDone ? (
                        <div style={{
                            fontSize: '9px', color: c.text, fontStyle: 'italic',
                            lineHeight: '1.4', padding: '4px 8px',
                            backgroundColor: c.border + '15', borderRadius: '6px',
                            borderLeft: `2px solid ${c.border}`,
                            textAlign: 'left', maxWidth: '100%',
                            overflow: 'hidden', display: '-webkit-box',
                            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
                            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                        }}>
                            ↳ {comment}
                        </div>
                    ) : (
                        <div style={{
                            display: 'inline-block', fontSize: '9px', fontWeight: '700',
                            textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 8px',
                            borderRadius: '10px', backgroundColor: c.border + '25', color: c.text,
                            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                        }}>
                            {statusLabel || (status === 'active' ? '● In Progress' :
                             status === 'completed' ? '✓ Done' :
                             status === 'rejected' ? '✗ Rejected' :
                             status === 'returned' ? '↩ Returned' :
                             status === 'skipped' ? '— Skipped' : '○ Pending')}
                        </div>
                    )}
                </div>
            </div>

            {/* Timestamp below the node */}
            {(timestamp || assignedTime) && (
                <div style={{
                    fontSize: '10px', color: '#94a3b8', textAlign: 'center', lineHeight: '1.4',
                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                    marginTop: '4px', padding: '0 4px',
                }}>
                    {timestamp && <div style={{ fontWeight: '500', color: '#64748b' }}>{timestamp}</div>}
                </div>
            )}
        </div>
    );
};

// ─── Connector ───────────────────────────────────────────────────────────────

const VerticalConnector: React.FC<{ color?: string; height?: number }> = ({
    color = '#d0ccc9', height = 28,
}) => (
    <div style={{ width: '2px', height: `${height}px`, backgroundColor: color, margin: '0 auto' }} />
);

// ─── Main Popup ──────────────────────────────────────────────────────────────

export const WorkflowGraphPopup: React.FC<WorkflowGraphPopupProps> = ({
    isOpen, onClose, gatePassId, getAccessToken, requesterName, companyName,
}) => {
    const [graphData, setGraphData] = useState<WorkflowGraphData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchGraphData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const accessToken = await getAccessToken();
            // Fetch both workflow graph and audit trail in parallel
            const [graphResponse, auditResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/gatepass/${gatePassId}/workflow-graph`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` },
                }),
                fetch(`${API_BASE_URL}/gatepass/${gatePassId}/audit-trail`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` },
                }).catch(() => null), // Don't fail if audit trail endpoint doesn't exist
            ]);
            const result = await graphResponse.json();
            if (result.success) {
                // DEBUG: Log gate branch data to see what backend returns
                console.log('🔀 WorkflowGraph DEBUG: gateBranches from API:', JSON.stringify(result.data.gateBranches, null, 2));
                result.data.gateBranches?.forEach((b: any) => {
                    console.log(`  Gate ${b.gateNumber}: isExcluded=${b.isExcluded}, exclusionReason=${b.exclusionReason}, reviewerTasks=${b.reviewerTasks?.length}, approverTasks=${b.approverTasks?.length}`);
                    [...(b.reviewerTasks || []), ...(b.approverTasks || [])].forEach((t: any) => {
                        console.log(`    └─ taskId=${t.taskId}, status=${t.taskStatus}`);
                    });
                });

                // Merge audit trail if not already present in graph data
                if (!result.data.auditTrail && auditResponse && auditResponse.ok) {
                    try {
                        const auditResult = await auditResponse.json();
                        if (auditResult.success && auditResult.data) {
                            result.data.auditTrail = Array.isArray(auditResult.data) ? auditResult.data : auditResult.data.entries || [];
                        }
                    } catch (e) {
                        console.log('⚠️ Could not parse audit trail response:', e);
                    }
                }

                setGraphData(result.data);
            } else {
                setError(result.error || 'Failed to load workflow data');
            }
        } catch (err) {
            setError('Network error loading workflow data');
        } finally {
            setLoading(false);
        }
    }, [gatePassId, getAccessToken]);

    useEffect(() => {
        if (isOpen) {
            fetchGraphData();
        }
    }, [isOpen, fetchGraphData]);

    if (!isOpen) return null;

    const currentState = graphData?.currentState || '';
    const isRejected = currentState === 'REJECTED';
    const isReturned = currentState === 'RETURNED';

    // Use the chronological workflowPasses from backend (rounds-based rendering)
    const passes = graphData?.workflowPasses || [];
    const hasAnyReturn = passes.some(p => p.returnEvent);

    // Helper: detect if gate was skipped via "Proceed with Endorsed"
    const isSkippedByUser = (g: GateBranch) => {
        if (g.exclusionReason === 'CANCELLED_BY_USER') return true;
        if (g.isExcluded) {
            const allTasks = [...g.reviewerTasks, ...g.approverTasks];
            return allTasks.length > 0 && allTasks.every(t => t.taskStatus === 'CANCELLED_BY_USER');
        }
        return false;
    };

    // Helper: detect if gate was rejected by an approver/reviewer
    const isRejectedGate = (g: GateBranch) => {
        if (!g.isExcluded) return false;
        if (isSkippedByUser(g)) return false;
        // Check if exclusion reason indicates rejection
        if (g.exclusionReason && g.exclusionReason.startsWith('Rejected by')) return true;
        // Check if any task has REJECTED status
        const allTasks = [...g.reviewerTasks, ...g.approverTasks];
        return allTasks.some(t => t.taskStatus === 'REJECTED');
    };

    // Compute global gate stats (for legend)
    const allGateBranches = passes.flatMap(p => p.gateBranches || []);
    const hasAnySkippedGates = allGateBranches.some(g => g.isExcluded && isSkippedByUser(g));
    const hasAnyRejectedGates = allGateBranches.some(g => isRejectedGate(g));

    // DEBUG: Log computed results
    const allPassGates = passes.flatMap(p => p.gateBranches || []);
    const multiGate = allPassGates.filter(g => g.reviewerTasks.length > 0 || g.approverTasks.length > 0 || isSkippedByUser(g)).length > 1;
    console.log('🔀 WorkflowGraph DEBUG:', { passCount: passes.length, hasAnyReturn, multiGate });

    return (
        <>
            {/* Backdrop */}
            <div onClick={onClose} style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.45)',
                backdropFilter: 'blur(4px)', zIndex: 9998,
            }} />

            {/* Panel */}
            <div style={{
                position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                zIndex: 9999, width: '95vw',
                maxWidth: multiGate ? '1100px' : '580px',
                maxHeight: '90vh', backgroundColor: '#ffffff', borderRadius: '16px',
                boxShadow: '0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    padding: '20px 24px', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', flexShrink: 0,
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', color: '#F3694A' }}><NodeIcon type="workflow" size={20} color="#F3694A" /></span>
                            <span style={{
                                fontSize: '17px', fontWeight: '700', color: '#f1f5f9',
                                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                            }}>Workflow Progress</span>
                            <span style={{
                                backgroundColor: 'rgba(255,255,255,0.12)', color: '#94a3b8',
                                fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px',
                            }}>#{gatePassId}</span>
                        </div>
                        {(requesterName || companyName) && (
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                {requesterName}{companyName ? ` • ${companyName}` : ''}
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} style={{
                        width: '34px', height: '34px', border: 'none', borderRadius: '10px',
                        backgroundColor: 'rgba(255,255,255,0.08)', color: '#94a3b8', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px',
                    }}
                        onMouseOver={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
                        onMouseOut={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
                    >✕</button>
                </div>

                {/* Body */}
                <div style={{ padding: '28px 24px 32px', overflowY: 'auto', flex: 1 }}>
                    <style>{`
                        @keyframes subtlePulse {
                            0%, 100% { box-shadow: 0 0 0 4px rgba(59,130,246,0.10); }
                            50% { box-shadow: 0 0 0 8px rgba(59,130,246,0.05); }
                        }
                        @keyframes spin { to { transform: rotate(360deg); } }
                    `}</style>

                    {loading && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                            <div style={{ display: 'inline-block', width: '28px', height: '28px', border: '3px solid #e8e8e8', borderTopColor: '#F3694A', borderRadius: '50%', animation: 'spin 0.7s linear infinite', marginBottom: '12px' }} />
                            <div style={{ fontSize: '14px' }}>Loading workflow graph...</div>
                        </div>
                    )}

                    {error && (
                        <div style={{
                            textAlign: 'center', padding: '30px 20px', color: '#991b1b',
                            backgroundColor: '#fef2f2', borderRadius: '8px',
                        }}>
                            <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '12px' }}>{error}</div>
                            <button onClick={fetchGraphData} style={{
                                padding: '6px 16px', backgroundColor: '#ef4444', color: 'white',
                                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                            }}>Retry</button>
                        </div>
                    )}

                    {graphData && !loading && !error && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                            {/* ── SUBMITTER (initial submission) ── */}
                            <GraphNode
                                icon="submit" label="Submitted"
                                sublabel={graphData.submitter.companyName}
                                status="completed"
                                performer={graphData.submitter.name || graphData.submitter.email?.split('@')[0]}
                                timestamp={formatTime(graphData.submitter.submittedAt)}
                            />

                            {/* ══════════════════════════════════════════════════════
                                ROUNDS-BASED RENDERING
                                
                                Each "pass" is one complete trip through the workflow.
                                After a RETURN + RESUBMIT, a new pass begins.
                                Each pass renders whatever stages actually occurred:
                                Company Approver → Company Reviewer → Gates → Security
                                
                                This is fully chronological — no fixed-order assumptions.
                                The sequence can repeat any number of times.
                               ══════════════════════════════════════════════════════ */}
                            {passes.map((pass, pIdx) => {
                                const isLastPass = pIdx === passes.length - 1;

                                // ── Split CA/CR tasks into sequential stages ──
                                const companyStages = splitPassIntoStages(pass.companyApprovers, pass.companyReviewers);
                                const multiStage = companyStages.length > 1;
                                const reviewerCompany = pass.reviewerCompanyName || pass.companyReviewers[0]?.assignedCompanyName || pass.companyName;
                                //const isDiffCompany = reviewerCompany && reviewerCompany !== pass.companyName;

                                // ── Gate branches for this pass ──
                                const passGateBranches = (pass.gateBranches || []).filter(g =>
                                    g.reviewerTasks.length > 0 || g.approverTasks.length > 0 || isSkippedByUser(g) || isRejectedGate(g)
                                );
                                const hasGates = passGateBranches.length > 0;
                                const passSkippedGates = passGateBranches.filter(g => g.isExcluded && isSkippedByUser(g));
                                const passRejectedGates = passGateBranches.filter(g => isRejectedGate(g));
                                const passActiveBranches = passGateBranches.filter(g => !g.isExcluded);
                                const passHasSkippedGates = passSkippedGates.length > 0;
                                const passHasRejectedGates = passRejectedGates.length > 0;
                                const passHasExcludedGates = passHasSkippedGates || passHasRejectedGates;

                                // lastCompanyDone: determined from the LAST stage's tasks
                                const lastStage = companyStages[companyStages.length - 1];
                                const lastCompanyDone = lastStage
                                    ? (lastStage.reviewers.length > 0
                                        ? lastStage.reviewers.some(t => t.taskStatus === 'COMPLETED')
                                        : lastStage.approvers.some(t => t.taskStatus === 'COMPLETED'))
                                    : false;

                                // ── Security status ──
                                const hasSecurity = pass.securityTasks.length > 0;
                                let secStatus = hasSecurity
                                    ? resolveTaskGroupStatus(pass.securityTasks, currentState, ['SECURITY_APPROVER_PENDING'])
                                    : null;
                                if (secStatus && pass.returnEvent?.stage === 'SECURITY_APPROVER' && secStatus === 'completed') {
                                    secStatus = 'returned' as NodeStatus;
                                }
                                if (secStatus && isLastPass && isReturned && secStatus === 'active') {
                                    secStatus = 'returned' as NodeStatus;
                                }

                                const prevPassDone = pIdx === 0 ? true : !!passes[pIdx - 1]?.resubmitEvent;

                                return (
                                    <React.Fragment key={`pass-${pass.passNumber}`}>
                                        {/* ── Submitter resubmit node between passes ── */}
                                        {pass.resubmitEvent && (
                                            <>
                                                <VerticalConnector color="#F3694A" height={20} />
                                                <GraphNode
                                                    icon="submit" label="Submitter"
                                                    sublabel={graphData.submitter.companyName}
                                                    status="completed" statusLabel="✓ Resubmitted"
                                                    performer={pass.resubmitEvent.performer || graphData.submitter.name || graphData.submitter.email?.split('@')[0]}
                                                    timestamp={pass.resubmitEvent.timestamp ? formatTime(pass.resubmitEvent.timestamp) : undefined}
                                                />
                                            </>
                                        )}

                                        <VerticalConnector color={prevPassDone ? '#F3694A' : '#d0ccc9'} />

                                        {passes.length > 1 && (
                                            <div style={{
                                                fontSize: '9px', fontWeight: '700', textTransform: 'uppercase',
                                                letterSpacing: '0.1em', color: '#94a3b8', marginBottom: '4px',
                                                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                            }}>
                                                Cycle {pass.passNumber} • {pass.companyName}
                                            </div>
                                        )}

                                        {/* ── Render each CA→CR stage sequentially ── */}
                                        {companyStages.map((stage, sIdx) => {
                                            const isFirstStage = sIdx === 0;
                                            const hasApprovers = stage.approvers.length > 0;
                                            const hasReviewers = stage.reviewers.length > 0;

                                            // Approver status
                                            let caStatus = hasApprovers
                                                ? resolveTaskGroupStatus(stage.approvers, currentState, ['COMPANY_APPROVER_PENDING'])
                                                : null;
                                            if (caStatus && pass.returnEvent?.stage === 'COMPANY_APPROVER' && caStatus === 'completed') caStatus = 'returned' as NodeStatus;
                                            if (caStatus && isLastPass && isReturned && caStatus === 'active') caStatus = 'returned' as NodeStatus;
                                            if (caStatus && isLastPass && isRejected && caStatus === 'active') caStatus = 'rejected' as NodeStatus;

                                            // Reviewer status
                                            let crStatus = hasReviewers
                                                ? resolveTaskGroupStatus(stage.reviewers, currentState, ['COMPANY_REVIEWER_PENDING'])
                                                : null;
                                            if (crStatus && pass.returnEvent?.stage === 'COMPANY_REVIEWER' && crStatus === 'completed') crStatus = 'returned' as NodeStatus;
                                            if (crStatus && isLastPass && isReturned && crStatus === 'active') crStatus = 'returned' as NodeStatus;
                                            if (crStatus && isLastPass && isRejected && crStatus === 'active') crStatus = 'rejected' as NodeStatus;

                                            const caDone = hasApprovers && stage.approvers.some(t => t.taskStatus === 'COMPLETED');
                                            const connectorAfterCA = caDone ? '#F3694A' : '#d0ccc9';

                                            // Per-stage reviewer company
                                            const stageReviewerCompany = stage.reviewers[0]?.assignedCompanyName || reviewerCompany;

                                            // Stage label suffixes for multi-stage passes
                                            // First approver (from submitter's company) has no stage.
                                            // Each reviewer starts a new stage number.
                                            // The approver AFTER a reviewer shares that reviewer's stage number.
                                            const approverStageTag = multiStage && sIdx > 0 ? ` (Stage ${sIdx})` : '';
                                            const reviewerStageTag = multiStage ? ` (Stage ${sIdx + 1})` : '';

                                            // Connector color from previous stage
                                            const prevStageDone = !isFirstStage && (() => {
                                                const prev = companyStages[sIdx - 1];
                                                return prev.reviewers.length > 0
                                                    ? prev.reviewers.some(t => t.taskStatus === 'COMPLETED')
                                                    : prev.approvers.some(t => t.taskStatus === 'COMPLETED');
                                            })();
                                            const connectorFromPrev = prevStageDone ? '#F3694A' : '#d0ccc9';

                                            return (
                                                <React.Fragment key={`stage-${sIdx}`}>
                                                    {/* Connector between stages (not before the first stage) */}
                                                    {!isFirstStage && (
                                                        <VerticalConnector color={connectorFromPrev} height={20} />
                                                    )}

                                                    {/* Company Approver */}
                                                    {hasApprovers && caStatus !== null && (() => {
                                                        const approverCompany = sIdx > 0
                                                            ? (stage.approvers[0]?.assignedCompanyName || stageReviewerCompany)
                                                            : pass.companyName;
                                                        return (
                                                            <GraphNode icon="company"
                                                                label={`Company Approver${approverStageTag}`}
                                                                sublabel={approverCompany}
                                                                status={caStatus}
                                                                performer={getPerformerName(stage.approvers)}
                                                                timestamp={getCompletedTime(stage.approvers)}
                                                                comment={getComments(stage.approvers)}
                                                                tasks={stage.approvers} />
                                                        );
                                                    })()}

                                                    {/* Company Reviewer with review stage divider */}
                                                    {hasReviewers && crStatus !== null && (
                                                        <>
                                                            {hasApprovers && (
                                                                <>
                                                                    <VerticalConnector color={connectorAfterCA} height={20} />
                                                                    <div style={{
                                                                        display: 'flex', alignItems: 'center', gap: '12px',
                                                                        width: '100%', maxWidth: '220px', margin: '0 auto 8px',
                                                                    }}>
                                                                        <div style={{ flex: 1, height: '2px', backgroundColor: connectorAfterCA }} />
                                                                        <div style={{
                                                                            fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
                                                                            letterSpacing: '0.08em', color: '#94a3b8', whiteSpace: 'nowrap',
                                                                            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                                        }}>
                                                                            {multiStage || stageReviewerCompany !== pass.companyName
                                                                                ? `→ ${stageReviewerCompany}`
                                                                                : 'Review Stage'}
                                                                        </div>
                                                                        <div style={{ flex: 1, height: '2px', backgroundColor: connectorAfterCA }} />
                                                                    </div>
                                                                    <VerticalConnector color={connectorAfterCA} height={12} />
                                                                </>
                                                            )}
                                                            <GraphNode icon="reviewer"
                                                                label={`Company Reviewer${reviewerStageTag}`}
                                                                sublabel={stageReviewerCompany}
                                                                status={crStatus}
                                                                performer={getPerformerName(stage.reviewers)}
                                                                timestamp={getCompletedTime(stage.reviewers)}
                                                                comment={getComments(stage.reviewers)}
                                                                tasks={stage.reviewers} />
                                                        </>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}

                                        {/* ── Gate Branches ── */}
                                        {hasGates && (() => {
                                            const branchCount = passGateBranches.length;
                                            return (
                                                <>
                                                    <VerticalConnector color={lastCompanyDone ? '#F3694A' : '#d0ccc9'} height={20} />
                                                    <div style={{
                                                        fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
                                                        letterSpacing: '0.08em', color: '#94a3b8', marginBottom: '8px',
                                                    }}>
                                                        {branchCount > 1 ? `${branchCount} Gate Branches` : 'Gate Processing'}
                                                    </div>

                                                    <div style={{
                                                        display: 'flex', justifyContent: 'center', gap: '16px',
                                                        flexWrap: 'wrap', padding: '12px 0', position: 'relative',
                                                    }}>
                                                        {branchCount > 1 && (
                                                            <div style={{
                                                                position: 'absolute', top: '0', left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                width: `${Math.min(branchCount * 160, 700)}px`,
                                                                maxWidth: '90%', height: '2px',
                                                                backgroundColor: lastCompanyDone ? '#F3694A' : '#d0ccc9',
                                                            }} />
                                                        )}

                                                        {passGateBranches.map(branch => {
                                                            const isSkipped = isSkippedByUser(branch);
                                                            const isRejected = isRejectedGate(branch);
                                                            const isDimmed = isSkipped || isRejected;
                                                            // Allow gate reviewer to be active when workflow is in any gate processing state
                                                            const revSt = isSkipped ? 'skipped' as NodeStatus
                                                                : resolveTaskGroupStatus(branch.reviewerTasks, currentState, ['GATE_REVIEWER_PENDING', 'GATE_APPROVER_PENDING']);
                                                            // Gate approver: active in any gate state, but only if this branch's reviewer is done
                                                            const branchReviewerDone = branch.reviewerTasks.length > 0
                                                                && branch.reviewerTasks.some(t => t.taskStatus === 'COMPLETED')
                                                                && !branch.reviewerTasks.some(t => t.taskStatus === 'PENDING');
                                                            const appSt = branch.approverTasks.length > 0
                                                                ? (isSkipped ? 'skipped' as NodeStatus
                                                                    : resolveTaskGroupStatus(branch.approverTasks, currentState,
                                                                        branchReviewerDone ? ['GATE_APPROVER_PENDING', 'GATE_REVIEWER_PENDING'] : ['GATE_APPROVER_PENDING']))
                                                                : null;

                                                            return (
                                                                <div key={branch.gateId} style={{
                                                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                                                    backgroundColor: isRejected ? '#fef2f2' : isSkipped ? '#f0f0f0' : '#f7f7f7',
                                                                    border: `1px solid ${isRejected ? '#fca5a5' : isSkipped ? '#d4d4d4' : '#d4d4d4'}`,
                                                                    borderRadius: '14px', padding: '16px 12px 12px', minWidth: '140px',
                                                                    opacity: isDimmed ? 0.65 : 1, position: 'relative',
                                                                }}>
                                                                    <div style={{
                                                                        fontSize: '10px', fontWeight: '700',
                                                                        color: isDimmed ? '#9ca3af' : '#4a4a4a',
                                                                        backgroundColor: isRejected ? '#fee2e2' : isSkipped ? '#e8e8e8' : '#e8e8e8',
                                                                        padding: '2px 10px', borderRadius: '20px', marginBottom: '10px',
                                                                        textDecoration: isDimmed ? 'line-through' : 'none',
                                                                    }}>Gate {branch.gateNumber}</div>

                                                                    {isRejected && (
                                                                        <div style={{
                                                                            fontSize: '9px', fontWeight: '700', color: '#991b1b',
                                                                            backgroundColor: '#fee2e2', padding: '2px 8px',
                                                                            borderRadius: '8px', marginBottom: '8px', letterSpacing: '0.04em',
                                                                        }}>✕ REJECTED</div>
                                                                    )}

                                                                    {isSkipped && (
                                                                        <div style={{
                                                                            fontSize: '9px', fontWeight: '700', color: '#92400e',
                                                                            backgroundColor: '#fef3c7', padding: '2px 8px',
                                                                            borderRadius: '8px', marginBottom: '8px', letterSpacing: '0.04em',
                                                                        }}>— SKIPPED</div>
                                                                    )}
                                                                    {isSkipped && branch.reviewerTasks.length === 0 && branch.approverTasks.length === 0 && (
                                                                        <div style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic', padding: '4px 0', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
                                                                            Cancelled by user
                                                                        </div>
                                                                    )}

                                                                    {branch.reviewerTasks.length > 0 && (
                                                                        <>
                                                                            <GraphNode compact icon="gate-review" label="Gate Reviewer"
                                                                                sublabel={`Gate ${branch.gateNumber}`} status={revSt}
                                                                                performer={getPerformerName(branch.reviewerTasks)}
                                                                                timestamp={isSkipped ? undefined : getCompletedTime(branch.reviewerTasks)}
                                                                                comment={isSkipped ? undefined : getComments(branch.reviewerTasks)}
                                                                                tasks={branch.reviewerTasks} />
                                                                            {appSt !== null && (
                                                                                <VerticalConnector color={isSkipped ? '#d4d4d4' : (revSt === 'completed' ? '#F3694A' : '#d0ccc9')} height={16} />
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {appSt !== null && (
                                                                        <GraphNode compact icon="gate-approve" label="Gate Approver"
                                                                            sublabel={`Gate ${branch.gateNumber}`} status={appSt}
                                                                            performer={isSkipped ? undefined : getPerformerName(branch.approverTasks)}
                                                                            timestamp={isSkipped ? undefined : getCompletedTime(branch.approverTasks)}
                                                                            comment={isSkipped ? undefined : getComments(branch.approverTasks)}
                                                                            tasks={branch.approverTasks} />
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>

                                                    {(() => {
                                                        // Check if all active (non-excluded, non-rejected) branches are fully done
                                                        const activeBranches = passGateBranches.filter(b => !b.isExcluded && !isRejectedGate(b));
                                                        const allActiveBranchesDone = activeBranches.every(b =>
                                                            (b.approverTasks.length === 0 || b.approverTasks.some(t => t.taskStatus === 'COMPLETED')) &&
                                                            (b.reviewerTasks.length === 0 || b.reviewerTasks.some(t => t.taskStatus === 'COMPLETED'))
                                                        );

                                                        // Show "Proceeded with Approved Gates" only when there are excluded/rejected gates AND all remaining active branches are done
                                                        if (passHasExcludedGates && allActiveBranchesDone) {
                                                            return (
                                                                <>
                                                                    <VerticalConnector color={passHasRejectedGates ? '#ef4444' : '#f59e0b'} height={12} />
                                                                    <div style={{
                                                                        display: 'flex', alignItems: 'center', gap: '8px',
                                                                        backgroundColor: passHasRejectedGates ? '#fef2f2' : '#fffbeb',
                                                                        border: `1.5px solid ${passHasRejectedGates ? '#fca5a5' : '#f59e0b'}`,
                                                                        borderRadius: '10px', padding: '8px 16px',
                                                                        boxShadow: `0 0 0 3px ${passHasRejectedGates ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)'}`,
                                                                    }}>
                                                                        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{passHasRejectedGates ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>}</span>
                                                                        <div>
                                                                            <div style={{ fontSize: '11px', fontWeight: '700', color: passHasRejectedGates ? '#991b1b' : '#92400e', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>Proceeded with Approved Gates</div>
                                                                            <div style={{ fontSize: '9px', color: passHasRejectedGates ? '#b91c1c' : '#b45309', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
                                                                                {passActiveBranches.length} gate{passActiveBranches.length !== 1 ? 's' : ''} approved
                                                                                {passRejectedGates.length > 0 ? ` · ${passRejectedGates.length} gate${passRejectedGates.length !== 1 ? 's' : ''} rejected` : ''}
                                                                                {passSkippedGates.length > 0 ? ` · ${passSkippedGates.length} gate${passSkippedGates.length !== 1 ? 's' : ''} skipped` : ''}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <VerticalConnector color={passHasRejectedGates ? '#ef4444' : '#f59e0b'} height={12} />
                                                                </>
                                                            );
                                                        }

                                                        // Otherwise show regular connector bar
                                                        const allGatesDone = !passHasExcludedGates && activeBranches.every(b =>
                                                            (b.approverTasks.length === 0 || b.approverTasks.some(t => t.taskStatus === 'COMPLETED')) &&
                                                            (b.reviewerTasks.length === 0 || b.reviewerTasks.some(t => t.taskStatus === 'COMPLETED'))
                                                        );
                                                        const barColor = allGatesDone ? '#F3694A' : '#d0ccc9';
                                                        return (
                                                            <>
                                                                <VerticalConnector color={barColor} height={12} />
                                                                <div style={{
                                                                    width: `${Math.min(branchCount * 160, 900)}px`, maxWidth: '90%',
                                                                    height: '2px', backgroundColor: barColor, margin: '0 auto',
                                                                }} />
                                                                <VerticalConnector color={barColor} height={12} />
                                                            </>
                                                        );
                                                    })()}
                                                </>
                                            );
                                        })()}

                                        {/* ── Security Approver ── */}
                                        {hasSecurity && secStatus !== null && (
                                            <>
                                                {!hasGates && <VerticalConnector color={lastCompanyDone ? '#F3694A' : '#d0ccc9'} />}
                                                <GraphNode icon="security" label="Security Approver"
                                                    status={secStatus}
                                                    performer={getPerformerName(pass.securityTasks)}
                                                    timestamp={getCompletedTime(pass.securityTasks)}
                                                    comment={getComments(pass.securityTasks)}
                                                    tasks={pass.securityTasks} />
                                            </>
                                        )}
                                    </React.Fragment>
                                );
                            })}

                            {/* ── FINAL NODE ── */}
                            <VerticalConnector color={
                                isReturned || isRejected || currentState === 'FULLY_APPROVED' ? '#F3694A' : '#d0ccc9'
                            } />
                            {isReturned ? (
                                <GraphNode
                                    icon="submit" label="Submitter"
                                    sublabel={graphData.submitter.companyName}
                                    status="active"
                                    performer={graphData.submitter.name || graphData.submitter.email?.split('@')[0]}
                                />
                            ) : (
                                <GraphNode
                                    icon={currentState === 'FULLY_APPROVED' ? 'approved' : isRejected ? 'rejected' : 'finish'}
                                    label={currentState === 'FULLY_APPROVED' ? 'Accepted for Processing' :
                                           isRejected ? 'Rejected' : 'Accepted for Processing'}
                                    status={currentState === 'FULLY_APPROVED' ? 'completed' :
                                            isRejected ? 'rejected' : 'pending'} />
                            )}
                        </div>
                    )}

                    {/* Legend */}
                    {graphData && !loading && (
                        <div style={{
                            display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap',
                            marginTop: '28px', paddingTop: '16px', borderTop: '1px solid #f1f5f9',
                        }}>
                            {([
                                { status: 'completed' as NodeStatus, label: 'Completed' },
                                { status: 'active' as NodeStatus, label: 'In Progress' },
                                { status: 'pending' as NodeStatus, label: 'Pending' },
                                ...(isRejected ? [{ status: 'rejected' as NodeStatus, label: 'Rejected' }] : []),
                                ...((isReturned || hasAnyReturn) ? [{ status: 'returned' as NodeStatus, label: 'Returned' }] : []),
                                ...(hasAnySkippedGates ? [{ status: 'skipped' as NodeStatus, label: 'Skipped by User' }] : []),
                                ...(hasAnyRejectedGates && !isRejected ? [{ status: 'rejected' as NodeStatus, label: 'Gate Rejected' }] : []),
                            ]).map(item => (
                                <div key={item.status} style={{
                                    display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b',
                                }}>
                                    <div style={{
                                        width: '10px', height: '10px', borderRadius: '3px',
                                        backgroundColor: STATUS_COLORS[item.status].border,
                                    }} />
                                    {item.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default WorkflowGraphPopup;